// mantine
import {
    Button,
    TagsInput,
    TextInput,
    Textarea,
    UnstyledButton
} from '@mantine/core';
// utils
import { useFormik } from 'formik';
import type { TWebhook, TWebhookData } from '@newo/api/gateway';
import { processFormData } from '@newo/toolkit/bff';
import {
    useWebhookCreateMutation,
    useWebhookUpdateMutation
} from '@/services/webhooks/webhooks.queries.ts';
import validationSchema from '@/pages/Integrations/components/Webhooks/components/WebhookForm/WebhookForm.schema.ts';

interface WebhookFormProps {
    webhook?: TWebhook;
    connectorIdn: string;
    onClose?: () => void;
    onSubmit?: (data: TWebhookData) => void;
}

const WebhookForm = ({
    webhook,
    connectorIdn,
    onClose,
    onSubmit
}: WebhookFormProps) => {
    const mutationAdd = useWebhookCreateMutation();
    const mutationUpdate = useWebhookUpdateMutation();

    const initialValues: TWebhookData = {
        idn: webhook?.idn ?? '',
        description: webhook?.description ?? '',
        url: webhook?.url ?? '',
        connector_idn: webhook?.connector_idn ?? connectorIdn,
        command_idns: webhook?.command_idns ?? []
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: (formData) => {
            const data = processFormData(formData);
            if (webhook) {
                mutationUpdate.mutate(
                    { webhookId: webhook.id, data },
                    {
                        onSuccess() {
                            onSubmit?.(data);
                            onClose?.();
                        },
                        onSettled() {
                            formik.setSubmitting(false);
                        }
                    }
                );
            } else {
                mutationAdd.mutate(data, {
                    onSuccess() {
                        onSubmit?.(data);
                        onClose?.();
                    },
                    onSettled() {
                        formik.setSubmitting(false);
                    }
                });
            }
        }
    });

    const handleCancel = () => {
        onClose?.();
    };

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            {!webhook?.idn && (
                <TextInput
                    withAsterisk
                    error={
                        formik.touched.idn && formik.values.idn
                            ? formik.errors.idn
                            : undefined
                    }
                    label="Idn"
                    name="idn"
                    placeholder="Idn"
                    value={formik.values.idn}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                />
            )}
            <TextInput
                withAsterisk
                error={
                    formik.touched.url && formik.values.url
                        ? formik.errors.url
                        : undefined
                }
                label="URL"
                name="url"
                placeholder="URL"
                value={formik.values.url}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <Textarea
                error={
                    formik.touched.description && formik.values.description
                        ? formik.errors.description
                        : undefined
                }
                label="Description"
                name="description"
                placeholder="Description"
                value={formik.values.description}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <TagsInput
                error={formik.errors.command_idns}
                label="Commands"
                placeholder="Type and press Enter to add"
                value={formik.values.command_idns}
                onChange={(commandIdns) => {
                    formik.setFieldValue('command_idns', commandIdns);
                }}
            />

            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={handleCancel}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                    }
                    size="xs"
                    type="submit"
                >
                    {webhook ? 'Update' : 'Create'}
                </Button>
            </div>
        </form>
    );
};

export default WebhookForm;
